/** @module Strings */

import { first, isObject, last, cloneDeep, camelCase, deburr, toLower } from 'lodash';
import { VALID_EMAIL_REGEX } from 'libs/utils/constants';

/** @const {RegEx} RE_IS_BINARY Used to detect binary string values. */
const RE_IS_BINARY = /^0b[01]+$/i;

/** @const {RegEx} RE_IS_OCTAL Used to detect octal string values. */
const RE_IS_OCTAL = /^0o[0-7]+$/i;

/** @const {RegEx} RE_IS_BAD_HEX Used to detect bad signed hexadecimal string values. */
const RE_IS_BAD_HEX = /^[-+]0x[0-9a-f]+$/i;

/**
 * Checks if a given value is not a string, or is empty or whitespace-only.
 *
 * @param {any} value - The value to check.
 *
 * @returns {boolean} - Returns `true` if the value is not a non-empty string, otherwise `false`.
 */
export function isNotValidString(value) {
    return typeof value !== 'string' || value.trim().length === 0;
}

/**
 * Checks if a given value is a non-empty string (whitespace-only counts as empty).
 *
 * @param {any} value - The value to check.
 *
 * @returns {boolean} - Returns `true` if the value is a non-empty string, otherwise `false`.
 */
export function isValidString(value) {
    return !isNotValidString(value);
}

/**
 * Transforms first letter of the given value to a capital letter.
 *
 * @param {String} value the string to capitalize.
 * @return {String} the given string with a capitalized first letter.
 */
export function capitalize(value) {
    if (isNotValidString(value)) {
        return '';
    }
    value = value.toString();
    return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Joins the values of the given array in a human readable way.
 *
 * @param {Array<String>} values the strings array to concatenate.
 * @param {String|import('vue-i18n').TranslateResult} [conjunction=''] the conjunction word to use for the last element.
 * @param {String} [separator=', '] the normal separator.
 *
 * @return {String} the joined values
 */
export function humanJoin(values = [], conjunction = '', separator = ', ') {
    const val = cloneDeep(values);
    const last = val.splice(-1, 1)[0];

    if (val.length < 1) {
        return last || '';
    }

    conjunction = (conjunction || '').length === 0 ? separator : ` ${conjunction} `;

    const concat = [];

    concat.push(val.join(separator));
    concat.push(`${conjunction}${last}`);

    return concat.join('');
}

/**
 * Strips HTML from string
 *
 * @param {String} content the HTML string to strip tags from.
 *
 * @returns {String} the whole text without HTML tags.
 */
export function stripHTML(content) {
    if (isNotValidString(content)) {
        return '';
    }

    return (content || '')
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script\s*>/gi, '')
        .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style\s*>/gi, '')
        .replace(/(<([^>]{1,500})>)/gi, '');
}

/**
 * Given an name string this filter returns a string representing the user's initials.
 *
 * @param {String} [name=''] the name to get initials for.
 *
 * @return {String} the capitalized user initials or an empty string
 */
export function initials(name = '') {
    const names = untokenize(name.trim()).split(/\s/);

    // Could happen if `user` is a single letter string, or the user object
    // only defines values without spaces.
    if (names.length === 1) {
        return first(names).charAt(0).toLocaleUpperCase();
    }

    // All other case
    return `${first(names).charAt(0)}${last(names).charAt(0)}`.toLocaleUpperCase();
}

/**
 * Sanitizes translation key by replacing dots with underscores
 *
 * @param {String} key translation key
 *
 * @return {String} sanitized key
 */
export function sanitizeTranslationKey(key) {
    return String(key).replace(/\./g, '_');
}

/**
 * Transforms the given value into a dasherized string.
 * i.e. "SomeStringTesting" becomes "some-string-testing".
 *
 * @param {String} string the string to transform case from.
 *
 * @returns {String} a dasherized version of the given string.
 */
export function dasherize(string) {
    return toCase(string, '-');
}

/**
 * Removes all punctuation symbols from the string leaving only alphanumeric characters.
 *
 * @param {String} [string=''] the string to untokenize
 * @param {String} [replacement=''] the string to replace tokens with
 * @param {boolean} [trimTokens=false] if true it will remove tokens from the start and end of the word
 * @returns {String} the given string without tokens.
 */
export function untokenize(string = '', replacement = '', trimTokens = false) {
    if (isNotValidString(string)) {
        return '';
    }
    const rep = replacement.length ? `( |${replacement}){2,}` : '( ){2,}';
    const adjacentChecker = replacement.length ? replacement : ' ';

    string = string
        // removes all characters except latin, greek and cyrillic letters and spaces
        .replace(/[^\w\s\u0370-\u03ff\u0400-\u04FF]|_/g, replacement)
        // remove all adjacents spaces
        .replace(new RegExp(rep, 'gm'), adjacentChecker);

    if (trimTokens) {
        string = string.replace(new RegExp(`^(${replacement})+|(${replacement})+$`, 'g'), '');
    }

    return string;
}

/**
 * Transform the given string into a one-word parameter suitable for URLs or CSS classes.
 *
 * @param {String} [string=''] the string to transform to a parameter
 *
 * @returns {String} the parameter's name string
 */
export function parameterize(string = '') {
    if (isNotValidString(string)) {
        return '';
    }
    string = untokenize(string, '-', true); // Remove all punctuations and unwanted tokens
    string = string.trim().replace(/[\s|\-]+/gm, '-'); // Remove all spaces, all eventual new lines, and replace them with dashes
    string = dasherize(string); // Low-case dasherize

    return string;
}

/**
 * Get a hash code (in the java sense) of a string (used for caching for instance)
 * See // https://werxltd.com/wp/2010/05/13/javascript-implementation-of-javas-string-hashcode-method/
 *
 * @param {String} string the string to get the hash from
 *
 * @returns {Number} a 32bit hash version of the string
 */
export function hashCode(string) {
    let hash = 0,
        i,
        chr;

    if (isNotValidString(string)) {
        return hash;
    }

    for (i = 0; i < string.length; i++) {
        chr = string.charCodeAt(i);
        hash = (hash << 5) - hash + chr;
        hash |= 0; // Convert to 32bit integer
    }
    return hash;
}

/**
 * Transforms the given string to a human readable form.
 *
 * @param {String} string the string to humanize
 * @param {boolean} [capitalizeOutput=false] - Whether to capitalize the first letter of the output.
 *
 * @returns {String} a humanized representation of the given string.
 */
export function humanize(string = '', capitalizeOutput = false) {
    if (isNotValidString(string)) {
        return '';
    }

    const humanReadableString = untokenize(toSnakeCase(string), ' ');
    return capitalizeOutput ? capitalize(humanReadableString) : humanReadableString;
}

/**
 * Checks if the given string is a valid email format.
 *
 * @param {String|null} string the email to validate
 *
 * @returns {Boolean} whether the given string is a valid email or not
 */
export function isValidEmail(string = '') {
    return Boolean(string?.match(VALID_EMAIL_REGEX));
}

/**
 * Clamps string to a specific length but does not cut the sentence in the middle of a word
 *
 * @param {string} str the string to clamp
 * @returns {string} a truncated version of the string with ellipsis
 */
export function clamp(str = '', maxStringLength = 100) {
    if (isNotValidString(str)) {
        return '';
    }

    if (str.length < maxStringLength) {
        return str;
    }

    const clampped = str.split(/\s/).reduce((clampped, word) => {
        const isShort = (clampped + word).length < maxStringLength;

        return isShort ? `${clampped} ${word}` : clampped;
    }, '');

    return `${clampped}...`;
}

/**
 * @param {string} string
 *
 * @returns {string}
 */
export function toCamelCase(string) {
    return camelCase(string);
}

/**
 * Transforms the given string to its snake case format
 *
 * @param {string} string the string to transform to snake case
 *
 * @returns {string} the snake case version of the given string
 */
export function toSnakeCase(string) {
    return toCase(string, '_');
}

/**
 * Transforms the given string to a specified case format using a separator.
 *
 * @param {string} string - The string to transform.
 * @param {string} separator - The separator to use for the transformation.
 *
 * @returns {string} - The transformed string.
 *
 * @private
 */
function toCase(string, separator) {
    if (isNotValidString(string)) {
        return '';
    }

    return string
        .replace(/[\W_]+/g, separator) // Replace special characters with separator
        .replace(/([a-z])([A-Z])/g, `$1${separator}$2`) // Add separator between camelCase words
        .replace(/([A-Z]+)([A-Z][a-z])/g, `$1${separator}$2`) // Handle repeated capital letters
        .replace(/(\d+)/g, `${separator}$1${separator}`) // Treat numbers as words
        .replace(new RegExp(`${separator}+`, 'g'), separator) // Replace multiple separators with a single separator
        .replace(new RegExp(`^${separator}|${separator}$`, 'g'), '') // Remove leading or trailing separators
        .toLowerCase();
}

/**
 * Shrinks a string padding it in the edges
 *
 * @param {string} string the string to shrink
 * @param {number} [max=16] the maximum string length
 * @param {number} [pad=6] the length of the edges to keep
 * @param {string} [separator='...'] the separator of the edges
 *
 * @returns {string} the shrinked version of the strings
 */
export function shrink(string, max = 16, pad = 6, separator = '...') {
    if (isNotValidString(string)) {
        return '';
    }

    const len = string.length;

    if (len < max) {
        return string;
    }

    if (pad + pad + separator.length >= max) {
        return string;
    }

    return `${string.substring(0, pad)}${separator}${string.substring(len - pad, len)}`;
}

/**
 * Converts `value` to a number.
 *
 * @param {*} value The value to process.
 *
 * @returns {number} Returns the number.
 *
 * @example
 *
 * toNumber(Number.MIN_VALUE);
 * // => 5e-324
 *
 * toNumber(Infinity);
 * // => Infinity
 *
 * toNumber('3.2');
 * // => 3.2
 */
export function toNumber(value) {
    if (typeof value === 'number') {
        return value;
    }

    if (typeof value === 'symbol') {
        return NaN;
    }

    if (isObject(value)) {
        const other = typeof value.valueOf === 'function' ? value.valueOf() : value;
        value = isObject(other) ? `${other} ` : other;
    }

    if (typeof value !== 'string') {
        return value === 0 ? value : +value;
    }

    value = value.trim();
    const isBinary = RE_IS_BINARY.test(value);

    if (isBinary || RE_IS_OCTAL.test(value)) {
        return parseInt(value.slice(2), isBinary ? 2 : 8);
    }

    if (RE_IS_BAD_HEX.test(value)) {
        return NaN;
    }

    const numeric = Number.parseFloat(value);
    const hasDecimal = numeric % 1 !== 0;

    if (hasDecimal) {
        return numeric;
    }

    return Number.parseInt(value);
}

/**
 * Converts a type metadata object into a human-readable string representation.
 *
 * @param {Object} typeMetadata - The metadata object containing type information.
 * @param {string} type - The type to be humanized.
 * @param {boolean} [capitalize=false] - Whether to capitalize the first letter of the output.
 *
 * @returns {string} The human-readable representation of the type.
 */
export function humanizeType(typeMetadata, type, capitalize = false) {
    // Empty string are a valid _type_representation
    if (typeMetadata._type_representation || typeMetadata._type_representation === '') {
        return typeMetadata._type_representation;
    }

    return humanize(type, capitalize);
}

/**
 * Replaces placeholders in a string with corresponding values from an object.
 * Every matched placeholder is replaced (repeated occurrences included). Placeholders with no
 * corresponding value in the object are left untouched, unless `keepUnresolved` is set to false.
 *
 * @param {string} string - The string containing placeholders in the format {{key}}.
 * @param {Object} object - An object containing key-value pairs where the key corresponds to the placeholder in the string.
 * @param {boolean} [keepUnresolved=true] - If false, placeholders with no matching key are emptied instead of left untouched.
 *
 * @returns {string} - The string with placeholders replaced by corresponding values from the object.
 */
export function compileWithObject(string, object, keepUnresolved = true) {
    if (isNotValidString(string)) {
        return '';
    }

    return string.replaceAll(/{{\s*(\w+)\s*}}/g, (match, key) => {
        if (keepUnresolved && !(key in object)) {
            return match;
        }
        return object[key] || '';
    });
}

/**
 * Replaces placeholders in nested string of a rawData object with corresponding values from a params object.
 *
 * @param {Object} string - An object containing nested strings containing placeholders in the format {{key}}.
 * @param {Object} params - An object containing key-value pairs where the key corresponds to the placeholder in the string.
 *
 * @returns {Object} - The string with placeholders replaced by corresponding values from the object.
 */
export function compileWithObjectDeep(rawObject, params) {
    const compiledObject = {};
    for (const [key, value] of Object.entries(rawObject)) {
        if (typeof value === 'object') {
            compiledObject[key] = compileWithObjectDeep(value, params);
        } else {
            compiledObject[key] = compileWithObject(value, params);
        }
    }
    return compiledObject;
}

/**
 * Lowercases, folds accents and collapses whitespace, so `muller` matches `Müller`.
 *
 * @param {String} value the value to normalize.
 * @returns {String} the normalized value.
 */
export function normalizeSearchTerm(value) {
    return deburr(toLower(value ?? ''))
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Locates a normalized term inside `text` so it can be highlighted. Each folded character
 * keeps its source offset, so one standing for several (`Æ` for `ae`) cannot shift the
 * match onto its neighbours.
 *
 * @param {String} text the text to search in.
 * @param {String} term the term, already normalized.
 *
 * @returns {{ before: String, match: String, after: String } | null} null when not found.
 */
export function splitOnSearchTerm(text, term) {
    const source = String(text ?? '');

    if (!term || !source) {
        return null;
    }

    let normalized = '';
    /** @type {number[]} source offset of each folded character */
    const offsets = [];

    for (let index = 0; index < source.length; index++) {
        const character = source[index];

        if (/\s/.test(character)) {
            // a whitespace run collapses into the term's single space
            if (normalized.length && !normalized.endsWith(' ')) {
                normalized += ' ';
                offsets.push(index);
            }
            continue;
        }

        const folded = deburr(character).toLowerCase();
        normalized += folded;
        for (let i = 0; i < folded.length; i++) {
            offsets.push(index);
        }
    }

    const at = normalized.indexOf(term);

    if (at < 0) {
        return null;
    }

    const start = offsets[at];
    const end = offsets[at + term.length] ?? source.length;

    return {
        before: source.slice(0, start),
        match: source.slice(start, end),
        after: source.slice(end)
    };
}
